import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { WaitingSpinner, ConditionalLeaderboard, GameEndedScreen, LoadingQuestion, getPlayerHeaderLeftButton } from '../shared';
import { useHostPlaying } from '../../hooks/useHostPlaying';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import '../shared/TimerBar.css';
import '../../App.css';
import { useTranslation } from '../../i18n';

interface CollaborativeQuestion {
  questionId: string;
  question: string;
  title: string;
  description: string;
  timeLimit: number;
  startTime: number;
  playerId: string;
  clues: Array<{
    clue: string;
    order: number;
  }>;
  totalClues: number;
  hasMoreClues?: boolean;
  questionNumber?: number;
  totalQuestions?: number;
  options?: string[]; // Multiple choice options
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  allCorrect?: boolean;
  allClues?: Array<{
    clue: string;
    order: number;
    type?: string;
  }>;
  teamScore?: number;
  interesting?: string;
}

interface CollaborativePlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: any;
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

const CollaborativePlayerDisplay: React.FC<CollaborativePlayerDisplayProps> = ({ roomState, socket, myPlayerId, onGoHome, onLoadingTimeout, isHost = false, onBackToGameSelection }) => {
  const { t } = useTranslation();
  const hostIsPlaying = useHostPlaying(roomState);
  const [collaborativeQuestion, setCollaborativeQuestion] = useState<CollaborativeQuestion | null>(null);
  // Removed textAnswer state - only multiple choice now
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<{
    mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
    wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
    totalQuestions: number;
    averageScore: number;
  } | null>(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [myScore, setMyScore] = useState(0);
  const [allPlayersAnswered, setAllPlayersAnswered] = useState(false);
  const [answeredPlayerIds, setAnsweredPlayerIds] = useState<Set<string>>(new Set());
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const isSubmittingRef = useRef<boolean>(false);
  const currentQuestionIdRef = useRef<string | undefined>(undefined);
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const hasReceivedQuestionRef = useRef<boolean>(false);

  const gameEndData = useGameEndScreenData(roomState);

  // Helper to reset question state
  const resetQuestion = () => {
    // Text answer removed
    setSelectedAnswer(null);
    setAnswerSubmitted(false);
    setAllPlayersAnswered(false);
    setAnsweredPlayerIds(new Set());
    setQuestionResult(null);
    isSubmittingRef.current = false;
  };

  // Sync score from roomState
  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const myPlayer = roomState.players.find(p => p.id === myPlayerId);
      if (myPlayer) {
        setMyScore(myPlayer.score);
      }
    }
  }, [roomState.players, myPlayerId]);

  // When server says game is finished (e.g. reconnection after missing game-ended), show game end screen
  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCollaborativeQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- gameEndData is derived from roomState
  }, [roomState.gameState, gameEndData]);

  // CRITICAL: Check if we have a question in roomState when component mounts
  // This handles the case where the question was sent before component mounted
  useEffect(() => {
    if (!hasReceivedQuestionRef.current && roomState.currentQuestion && (roomState.gameType === 'collaborative-puzzle' || roomState.gameType === 'story-chain')) {
      console.log('🎯 Found question in roomState on mount - creating fallback question!', roomState.currentQuestion);
      const q = roomState.currentQuestion;
      const questionId = q.questionId || q.id || 'unknown';
      
      // Create a fallback collaborative question from roomState
      const fallbackQuestion: CollaborativeQuestion = {
        questionId: questionId,
        question: q.question || '',
        title: q.title || 'Collaborative Puzzle',
        description: q.question || q.description || '',
        timeLimit: q.timeLimit || 60000,
        startTime: q.startTime || Date.now(),
        playerId: myPlayerId || socket.id || '',
        clues: [], // Clues will come via get-collaborative-clues response
        totalClues: 0,
        hasMoreClues: true,
        questionNumber: q.questionNumber,
        totalQuestions: q.totalQuestions,
        options: q.options || [], // Include options if available
      };
      
      hasReceivedQuestionRef.current = true;
      previousQuestionIdRef.current = fallbackQuestion.questionId;
      currentQuestionIdRef.current = fallbackQuestion.questionId;
      setCollaborativeQuestion(fallbackQuestion);
      setTimeRemaining(fallbackQuestion.timeLimit);
      resetQuestion();
      
      // CRITICAL: Request clues from backend since we missed the collaborative-question event
      if (socket && roomState.roomName) {
        console.log('📡 Requesting clues from backend for question', questionId);
        socket.emit('get-collaborative-clues', { 
          roomName: roomState.roomName,
          questionId: questionId,
        });
      }
    }
  }, [roomState.currentQuestion, roomState.gameType, socket, roomState.roomName, myPlayerId]);

  // Log when component mounts
  useEffect(() => {
    console.log('🎮 CollaborativePlayerDisplay MOUNTED - setting up event handlers');
    console.log(`   myPlayerId: ${myPlayerId}, socket.id: ${socket.id}`);
    return () => {
      console.log('🎮 CollaborativePlayerDisplay UNMOUNTED - cleaning up event handlers');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('🎮 CollaborativePlayerDisplay useEffect RUNNING - registering collaborative-question handler');
    console.log(`   myPlayerId: ${myPlayerId}, socket.id: ${socket.id}, hasReceivedQuestion: ${hasReceivedQuestionRef.current}`);
    
    const handleCollaborativeQuestion = (data: CollaborativeQuestion) => {
      console.log('CollaborativePlayerDisplay received collaborative-question:', data);
      console.log(`Clues received: ${data.clues?.length || 0} clues`, data.clues);
      
      // Get actual player ID - use myPlayerId if available, otherwise try socket.id
      const actualPlayerId = myPlayerId || socket.id;
      console.log(`Event playerId: ${data.playerId}, myPlayerId: ${myPlayerId}, socket.id: ${socket.id}, actualPlayerId: ${actualPlayerId}, hasReceivedQuestion: ${hasReceivedQuestionRef.current}`);
      
      const newQuestionId = data.questionId;
      const isNewQuestion = newQuestionId !== previousQuestionIdRef.current;
      
      // CRITICAL: If we haven't received ANY question yet, accept the FIRST one that comes in
      // regardless of playerId - this handles the race condition where myPlayerId isn't set yet
      if (!hasReceivedQuestionRef.current && isNewQuestion) {
        console.log(`✅ ACCEPTING FIRST question ever received - questionId: ${newQuestionId}, event playerId: ${data.playerId}, actualPlayerId: ${actualPlayerId}, clues: ${data.clues?.length || 0}`);
        hasReceivedQuestionRef.current = true;
        previousQuestionIdRef.current = newQuestionId;
        currentQuestionIdRef.current = newQuestionId;
        setCollaborativeQuestion(data);
        setTimeRemaining(data.timeLimit);
        resetQuestion();
        return;
      }
      
      // IMPORTANT: If we have a question but it has no clues (from fallback), and this event has clues,
      // update it with the clues - this handles the case where fallback question was used
      const currentQuestion = collaborativeQuestion;
      if (isNewQuestion && currentQuestion && (!currentQuestion.clues || currentQuestion.clues.length === 0) && data.clues && data.clues.length > 0) {
        console.log(`🔄 Updating question with clues! Had ${currentQuestion.clues?.length || 0} clues, now ${data.clues.length} clues`);
        // Check if playerId matches (or if myPlayerId is not set yet, accept it)
        if (actualPlayerId == null || data.playerId === actualPlayerId) {
          previousQuestionIdRef.current = newQuestionId;
          currentQuestionIdRef.current = newQuestionId;
          setCollaborativeQuestion(data);
          setTimeRemaining(data.timeLimit);
          return;
        }
      }
      
      // For subsequent questions (after we've received our first one), filter by playerId
      // BUT: In Cloudflare, playerId might be socketId, so compare with both myPlayerId and socket.id
      if (actualPlayerId != null && data.playerId !== actualPlayerId && data.playerId !== socket.id) {
        console.log(`⏭️ Skipping - wrong player (event playerId: ${data.playerId} vs my ID: ${actualPlayerId}, socket.id: ${socket.id})`);
        return;
      }
      
      // If myPlayerId is still null but we have a question already, check if this is the correct one
      // by comparing with socket.id if available
      if (actualPlayerId == null && hasReceivedQuestionRef.current) {
        console.log(`⚠️ Player ID still not set, but we already have a question. Skipping.`);
        return;
      }
      
      if (isNewQuestion) {
        console.log(`✅ Setting new collaborative question ${newQuestionId} with ${data.clues?.length || 0} clues and ${data.options?.length || 0} options for player ${actualPlayerId}`);
        previousQuestionIdRef.current = newQuestionId;
        currentQuestionIdRef.current = newQuestionId;
        setCollaborativeQuestion(data);
        setTimeRemaining(data.timeLimit);
        resetQuestion();
      } else {
        // Even if same question ID, ALWAYS update with personalized data (clues, options, etc.)
        // This handles the case where fallback question was set first, then personalized event arrives
        console.log(`🔄 Updating existing question ${newQuestionId} with personalized data (clues: ${data.clues?.length || 0}, options: ${data.options?.length || 0})`);
        setCollaborativeQuestion(prev => {
          if (!prev) {
            return data;
          }
          // Merge: use new data but preserve any existing data that's not in the new event
          // CRITICAL: Always use clues from new data if provided, otherwise keep existing clues
          return {
            ...prev,
            ...data,
            // Ensure clues are always updated if provided in new data
            clues: data.clues && data.clues.length > 0 ? data.clues : (prev.clues || []),
            // Ensure options are always updated if provided
            options: data.options && data.options.length > 0 ? data.options : prev.options,
          };
        });
      }
    };
    
    socket.on('collaborative-question', handleCollaborativeQuestion);
    console.log('✅ Event handler registered for collaborative-question');
    
    // Also listen on 'question' as fallback in case collaborative-question doesn't arrive
    // BUT: Don't create fallback for first question - wait for collaborative-question event which has clues
    const handleGenericQuestion = (data: any) => {
      console.log('⚠️ Received generic "question" event in CollaborativePlayerDisplay:', data);
      console.log(`   isCollaborative: ${data.isCollaborative}, gameType: ${roomState.gameType}, hasReceivedQuestion: ${hasReceivedQuestionRef.current}`);
      
      // If this is a collaborative puzzle and we don't have a question yet, DON'T create fallback
      // Wait for the collaborative-question event which includes clues
      // This prevents "Loading clues..." from showing for the first question
      const isCollaborativeGame = data.isCollaborative || roomState.gameType === 'collaborative-puzzle' || roomState.gameType === 'story-chain';
      
      // Only use generic question as fallback if we've already received a collaborative question before
      // This handles edge cases where collaborative-question might not arrive for subsequent questions
      if (hasReceivedQuestionRef.current && isCollaborativeGame) {
        console.log('⚠️ Using generic question as fallback for subsequent question');
        // For subsequent questions, we can use it as fallback, but clues should come via collaborative-clue-revealed
        const newQuestionId = data.questionId || data.id;
        if (newQuestionId !== currentQuestionIdRef.current) {
          const fallbackQuestion: CollaborativeQuestion = {
            questionId: newQuestionId,
            question: data.question || '',
            title: data.title || 'Collaborative Puzzle',
            description: data.question || data.description || '',
            timeLimit: data.timeLimit || 60000,
            startTime: data.startTime || Date.now(),
            playerId: myPlayerId || socket.id || '',
            clues: [], // Clues will come via collaborative-clue-revealed
            totalClues: data.totalClues || 0,
            hasMoreClues: true,
            questionNumber: data.questionNumber,
            totalQuestions: data.totalQuestions,
            options: data.options || [], // Include options if available
          };
          
          previousQuestionIdRef.current = fallbackQuestion.questionId;
          currentQuestionIdRef.current = fallbackQuestion.questionId;
          setCollaborativeQuestion(fallbackQuestion);
          setTimeRemaining(fallbackQuestion.timeLimit);
          resetQuestion();
        }
      } else {
        console.log(`⏭️ Skipping generic question - waiting for collaborative-question event with clues`);
      }
    };
    socket.on('question', handleGenericQuestion);
    console.log('✅ Event handler registered for generic "question" event (fallback)');

    const handleClueRevealed = (data: { questionId: string; playerId: string; clues: Array<{ clue: string; order: number }>; hasMoreClues?: boolean; options?: string[] }) => {
      console.log('🔔 CollaborativePlayerDisplay received collaborative-clue-revealed:', data);
      
      // Match by questionId - only process clues for the current question
      // NOTE: We don't need to check playerId because events are sent directly to the correct socket
      // The playerId field might be wrong, but since we're sending to the right socket, we can trust it
      if (data.questionId !== currentQuestionIdRef.current) {
        console.log(`Ignoring clue-revealed - wrong questionId (event: ${data.questionId}, current: ${currentQuestionIdRef.current})`);
        return;
      }
      
      console.log(`   ✅ Processing clue update for player ${myPlayerId}, question ${data.questionId}`);
      
      // Update collaborative question with new clues - PRESERVE ALL EXISTING DATA including options
      setCollaborativeQuestion(prev => {
        if (!prev) {
          // If no previous question, we can't properly handle this - should have received collaborative-question first
          console.warn('⚠️ Received collaborative-clue-revealed without existing question');
          return null;
        }
        
        if (prev.questionId !== data.questionId) {
          return prev;
        }
        
        // CRITICAL: Preserve ALL existing data, only update clues and hasMoreClues
        // If options are provided in the event, use them (they should always be included)
        return {
          ...prev,
          clues: data.clues,
          hasMoreClues: data.hasMoreClues,
          // Use options from event if provided, otherwise preserve existing
          options: data.options && data.options.length > 0 ? data.options : prev.options,
          // Title, description, etc. are all preserved via spread operator
        };
      });
    };

    const handleQuestionResult = (data: QuestionResult) => {
      console.log('Question result received:', data);
      setQuestionResult(data);
      setAllPlayersAnswered(true);
      const allAnsweredIds = new Set<string>();
      data.submissions.forEach(s => allAnsweredIds.add(s.playerId));
      setAnsweredPlayerIds(allAnsweredIds);
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: {
        mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
        wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
        totalQuestions: number;
        averageScore: number;
      };
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setCollaborativeQuestion(null);
      resetQuestion();
      hasReceivedQuestionRef.current = false; // Reset for new game
      previousQuestionIdRef.current = undefined;
      currentQuestionIdRef.current = undefined;
    };

    socket.on('collaborative-clue-revealed', handleClueRevealed);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);

    return () => {
      console.log('🧹 Cleaning up CollaborativePlayerDisplay event handlers');
      // Pass specific handler to only remove THIS component's listener, not App.tsx's
      socket.off('collaborative-question', handleCollaborativeQuestion);
      socket.off('question', handleGenericQuestion);
      socket.off('collaborative-clue-revealed', handleClueRevealed);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, myPlayerId, roomState.gameType]);

  useEffect(() => {
    if (collaborativeQuestion && timeRemaining > 0 && !answerSubmitted) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 100) {
            return 0;
          }
          return prev - 100;
        });
      }, 100);

      return () => clearInterval(timer);
    }
  }, [collaborativeQuestion, timeRemaining, answerSubmitted]);

  const handleSubmitAnswer = (answer?: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Use provided answer (from button click) - text input removed
    const answerToSubmit = answer;
    
    if (!answerToSubmit || answerSubmitted || isSubmittingRef.current || !socket || !roomState || !collaborativeQuestion) {
      return;
    }
    
    isSubmittingRef.current = true;
    setAnswerSubmitted(true);
    if (answer) {
      setSelectedAnswer(answer);
    }
    const clientTimestamp = Date.now();
    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: collaborativeQuestion.questionId,
      answer: answerToSubmit,
      timestamp: clientTimestamp,
    });
  };

  if (gameEnded) {
    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCollaborativeQuestion(null);
        resetQuestion();
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };
    const displayLeaderboard = finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? []);
    const displayStatistics = gameStatistics ?? gameEndData?.gameStatistics ?? null;

    return (
      <GameEndedScreen
        gameType={roomState.gameType}
        finalLeaderboard={displayLeaderboard}
        myPlayerId={myPlayerId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        showYouSuffix
        statistics={displayStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  // If game ended, show game ended screen (already handled above)
  // When roomState.gameState === 'finished' we sync gameEnded in useEffect above, so we may show GameEndedScreen

  if (!collaborativeQuestion && !gameEnded) {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        title={t('gameplay.loading.question')}
        onGoHome={onGoHome}
        isHost={isHost}
        onBackToGameSelection={onBackToGameSelection}
        onLongWaitTimeout={onLoadingTimeout}
      />
    );
  }
  
  // If game ended but no question, don't show loading
  if (!collaborativeQuestion && gameEnded) {
    return null;
  }

  // TypeScript guard: at this point, collaborativeQuestion must be non-null
  if (!collaborativeQuestion) {
    return null;
  }

  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = (timeRemaining / collaborativeQuestion.timeLimit) * 100;
  const sortedPlayers = roomState.players ? [...roomState.players].sort((a, b) => b.score - a.score) : [];

  // Get game type display name
  const gameTypeName = roomState.gameType === 'story-chain' ? '📖 Story Chain' : '🧩 Collaborative Puzzle';

  return (
    <div className="app">
      <Header
        leftButton={getPlayerHeaderLeftButton({
          isHost,
          onBackToGameSelection,
          onGoHome,
          backTooltip: t('gameplay.backToGameSelection'),
          homeTooltip: t('gameplay.leaveGame'),
        })}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage')
        }}
        centerContent={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
              <span className="header-game-name" style={{ fontSize: '1rem' }}>{gameTypeName}</span>
            <span style={{ 
              fontSize: '1.2rem', 
              fontWeight: 'bold', 
              color: '#34D399',
              padding: '0.3rem 0.6rem',
              backgroundColor: 'rgba(52, 211, 153, 0.1)',
              borderRadius: '6px',
              border: '2px solid #34D399',
            }}>
              {roomState.roomName}
            </span>
          </div>
        }
      />
      <div className="container player-content-wrapper" style={{ paddingTop: '3rem' }}>
        <ConditionalLeaderboard
          hostIsPlaying={hostIsPlaying}
          players={sortedPlayers}
          myPlayerId={myPlayerId}
        />

        <div className="question-container">
          {(() => {
            // Log clues for debugging (only in development)
            if (process.env.NODE_ENV === 'development') {
              console.log('🔍 Rendering clues - collaborativeQuestion.clues:', collaborativeQuestion.clues, 'length:', collaborativeQuestion.clues?.length);
            }
            return null;
          })()}
          {collaborativeQuestion.clues && collaborativeQuestion.clues.length > 0 ? (
            <>
              {collaborativeQuestion.clues.map((clueData, index) => (
                <div 
                  key={index} 
                  className="collaborative-clue-box player-surface-panel"
                  style={{
                    backgroundColor: 'rgba(var(--color-tertiary-rgb), 0.14)',
                    border: '3px solid var(--color-tertiary)',
                    borderRadius: '15px',
                    padding: '1.5rem',
                    marginBottom: collaborativeQuestion.clues.length > 1 && index < collaborativeQuestion.clues.length - 1 ? '1rem' : '1.5rem',
                    textAlign: 'center',
                    animation: index === collaborativeQuestion.clues.length - 1 ? 'fadeIn 0.5s ease-in' : 'none',
                  }}
                >
                  <div 
                    className="collaborative-clue-label"
                    style={{
                      fontSize: '0.8rem',
                      color: 'var(--color-tertiary)',
                      marginBottom: '0.8rem',
                      fontWeight: 'bold',
                    }}
                  >
                    YOUR CLUE #{clueData.order}
                  </div>
                  <div 
                    className="collaborative-clue-text"
                    style={{
                      fontSize: '1.2rem',
                      color: 'var(--text-primary)',
                      fontWeight: 'bold',
                    }}
                  >
                    {clueData.clue}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.2)',
              border: '2px solid #EF4444',
              borderRadius: '15px',
              padding: '1rem',
              marginBottom: '1.5rem',
              textAlign: 'center',
            }}>
              <p style={{ color: '#EF4444', fontSize: '0.9rem', margin: 0 }}>
                ⚠️ Loading clues... (If this persists, clues may not have been received)
              </p>
            </div>
          )}
          <div style={{
            fontSize: '0.85rem',
            color: 'var(--text-secondary)',
            textAlign: 'center',
            marginTop: '0.8rem',
            marginBottom: '0.5rem',
          }}>
            💬 Share your clues with teammates and work together to find the answer!
          </div>
        </div>
        
        {/* Timer slider above question title (no counter on player - avoids sync mismatch with host) */}
        <div style={{
          width: '100%',
          height: '10px',
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '5px',
          overflow: 'hidden',
          marginBottom: '1.5rem',
          marginTop: '1rem'
        }}>
          <div
            className={timeSeconds <= 5 ? 'timer-bar__slider-fill--warning' : ''}
            style={{
              width: `${progress}%`,
              height: '100%',
              background: timeSeconds <= 5 ? '#EF4444' : '#34D399',
              transition: 'width 0.1s linear'
            }}
          />
        </div>
        
        <div>
          {/* Always render options - backend always sends them */}
          {collaborativeQuestion.options && collaborativeQuestion.options.length > 0 && (
            <div className="options-list" style={{ 
              marginTop: '1rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.8rem',
            }}>
              {collaborativeQuestion.options.map((option, index) => {
                const letter = String.fromCharCode(65 + index); // A, B, C, D
                const isSelected = selectedAnswer === option;
                return (
                  <button
                    key={index}
                    type="button"
                    className="option-item"
                    onClick={() => {
                      if (!answerSubmitted && !isSubmittingRef.current) {
                        setSelectedAnswer(option);
                        handleSubmitAnswer(option);
                      }
                    }}
                    disabled={answerSubmitted || isSubmittingRef.current}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '1rem',
                      borderRadius: '10px',
                      border: isSelected ? '3px solid var(--color-tertiary)' : '2px solid rgba(248, 250, 252, 0.3)',
                      backgroundColor: isSelected 
                        ? 'rgba(var(--color-tertiary-rgb), 0.3)' 
                        : answerSubmitted 
                          ? 'rgba(248, 250, 252, 0.1)' 
                          : 'rgba(248, 250, 252, 0.15)',
                      color: '#fff',
                      textAlign: 'left',
                      cursor: answerSubmitted || isSubmittingRef.current ? 'not-allowed' : 'pointer',
                      opacity: answerSubmitted ? 0.6 : 1,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (!answerSubmitted && !isSubmittingRef.current && !isSelected) {
                        e.currentTarget.style.backgroundColor = 'rgba(248, 250, 252, 0.25)';
                        e.currentTarget.style.borderColor = 'rgba(248, 250, 252, 0.5)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!answerSubmitted && !isSubmittingRef.current && !isSelected) {
                        e.currentTarget.style.backgroundColor = 'rgba(248, 250, 252, 0.15)';
                        e.currentTarget.style.borderColor = 'rgba(248, 250, 252, 0.3)';
                      }
                    }}
                  >
                    <strong>{letter}.</strong> {option}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {answerSubmitted && (
          <div style={{ width: '100%', marginTop: '2rem' }}>
            {/* Mini team status at top - show who has answered */}
            <div style={{
              width: '100%',
              backgroundColor: 'rgba(15, 23, 42, 0.6)',
              borderRadius: '10px',
              padding: '0.8rem',
              marginBottom: '1rem',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: '0.8rem',
                flexWrap: 'wrap',
              }}>
                {roomState.players.map((player) => {
                  const isMe = player.id === myPlayerId;
                  const hasAnswered = answeredPlayerIds.has(player.id);
                  return (
                    <div 
                      key={player.id} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        padding: '0.3rem 0.6rem',
                        backgroundColor: isMe ? 'rgba(52, 211, 153, 0.2)' : hasAnswered ? 'rgba(52, 211, 153, 0.1)' : 'transparent',
                        borderRadius: '6px',
                        border: isMe ? '1px solid #34D399' : hasAnswered ? '1px solid rgba(52, 211, 153, 0.3)' : 'none',
                        opacity: hasAnswered ? 1 : 0.6,
                      }}
                    >
                      <span style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center' }}>
                        {player.avatar && (player.avatar.startsWith('/') || player.avatar.startsWith('data:') || player.avatar.includes('.svg')) 
                          ? <img src={player.avatar} alt="" style={{ width: '1rem', height: '1rem', objectFit: 'contain' }} />
                          : (player.avatar || '👤')
                        }
                      </span>
                      <span style={{ 
                        color: isMe ? '#34D399' : '#fff', 
                        fontSize: '0.85rem',
                        fontWeight: isMe ? 'bold' : 'normal',
                        maxWidth: '80px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {player.name}
                      </span>
                      {hasAnswered && (
                        <span style={{ color: '#34D399', fontSize: '0.8rem' }}>✓</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <WaitingSpinner isLoading={false} />

            {questionResult && allPlayersAnswered && (
              <div style={{
                width: '100%',
                marginTop: '2rem',
                padding: '2rem',
                backgroundColor: questionResult.allCorrect ? 'rgba(52, 211, 153, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                border: `3px solid ${questionResult.allCorrect ? '#34D399' : '#EF4444'}`,
                borderRadius: '15px',
                textAlign: 'center',
                animation: 'fadeIn 0.5s ease-in',
              }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  color: questionResult.allCorrect ? '#34D399' : '#EF4444',
                  fontWeight: 'bold',
                  marginBottom: '1rem',
                }}>
                  {questionResult.allCorrect ? '✅ Team Success!' : '❌ Not Quite Right'}
                </h3>
                <p style={{
                  fontSize: '1.2rem',
                  color: '#fff',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                }}>
                  {t('gameplay.collaborative.correctAnswer', { answer: questionResult.correctAnswer })}
                </p>
                {questionResult.teamScore !== undefined && questionResult.teamScore > 0 && (
                  <p style={{
                    fontSize: '1.1rem',
                    color: '#34D399',
                    fontWeight: 'bold',
                    marginBottom: '1rem',
                  }}>
                    {t('gameplay.collaborative.teamScore', { score: questionResult.teamScore })}
                  </p>
                )}
                {questionResult.interesting && (
                  <div style={{
                    marginTop: '1.5rem',
                    padding: '1rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '10px',
                  }}>
                    <p style={{
                      fontSize: '1rem',
                      color: '#fff',
                      lineHeight: '1.6',
                      margin: 0,
                    }}>
                      {questionResult.interesting}
                    </p>
                  </div>
                )}
                {questionResult.allClues && questionResult.allClues.length > 0 && (
                  <div style={{
                    marginTop: '1.5rem',
                    padding: '1rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '10px',
                  }}>
                    <p style={{
                      fontSize: '0.9rem',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: 'bold',
                      marginBottom: '0.5rem',
                    }}>
                      {t('gameplay.collaborative.allStoryClues')}
                    </p>
                    {questionResult.allClues.sort((a, b) => a.order - b.order).map((clue, index) => (
                      <p key={index} style={{
                        fontSize: '0.95rem',
                        color: '#fff',
                        margin: '0.3rem 0',
                        paddingLeft: '1rem',
                      }}>
                        {clue.order}. {clue.clue}
                      </p>
                    ))}
                  </div>
                )}
                <WaitingSpinner isLoading={true} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaborativePlayerDisplay;

